(() => {
  const SELECTOR = '[data-tvati-collection-gallery]';

  const initGallery = (gallery) => {
    if (!gallery || gallery.dataset.tvatiCollectionReady === 'true') return;

    const track = gallery.querySelector('[data-tvati-collection-track]');
    const slides = [...gallery.querySelectorAll('[data-tvati-collection-slide]')];
    const dots = [...gallery.querySelectorAll('[data-tvati-collection-dot]')];
    const previous = gallery.querySelector('[data-tvati-collection-prev]');
    const next = gallery.querySelector('[data-tvati-collection-next]');

    if (!track || slides.length < 2) {
      gallery.dataset.tvatiCollectionReady = 'true';
      return;
    }

    let activeIndex = 0;
    let frame = null;

    const setActive = (index) => {
      activeIndex = Math.max(0, Math.min(slides.length - 1, index));

      dots.forEach((dot, dotIndex) => {
        const active = dotIndex === activeIndex;
        dot.classList.toggle('is-active', active);

        if (active) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    };

    const scrollToIndex = (index) => {
      const target = Math.max(0, Math.min(slides.length - 1, index));

      track.scrollTo({
        left: track.clientWidth * target,
        behavior: 'smooth'
      });

      setActive(target);
    };

    const updateFromScroll = () => {
      window.cancelAnimationFrame(frame);

      frame = window.requestAnimationFrame(() => {
        if (!track.clientWidth) return;
        setActive(Math.round(track.scrollLeft / track.clientWidth));
      });
    };

    track.addEventListener('scroll', updateFromScroll, { passive: true });

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        scrollToIndex(Number(dot.dataset.tvatiCollectionDot || 0));
      });
    });

    previous?.addEventListener('click', () => {
      scrollToIndex(activeIndex <= 0 ? slides.length - 1 : activeIndex - 1);
    });

    next?.addEventListener('click', () => {
      scrollToIndex(activeIndex >= slides.length - 1 ? 0 : activeIndex + 1);
    });

    gallery.dataset.tvatiCollectionReady = 'true';
  };

  const init = (root = document) => {
    if (root.matches?.(SELECTOR)) initGallery(root);
    root.querySelectorAll?.(SELECTOR).forEach(initGallery);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });


  /* ==========================================================================
     BSS COLLECTION VARIANT PRICE RESOLVER

     BSS reliably rewrites its documented product-level collection price node.
     On this store the active B2B rules are percentage based, but BSS applies
     the collection price to the product rather than each variant. We derive
     the customer-specific factor from BSS's own rewritten probe and apply it
     to each card's real Shopify variant retail price.

     No discount percentage is hard-coded.
     ========================================================================== */

  const B2B_PRICE_SELECTOR = '[data-tvati-b2b-variant-price]';

  const parseMoneyText = (text) => {
    if (!text) return null;

    const cleaned = text
      .replace(/\s+/g, '')
      .replace(/[^\d.,-]/g, '');

    if (!cleaned) return null;

    const lastDot = cleaned.lastIndexOf('.');
    const lastComma = cleaned.lastIndexOf(',');
    let normalised = cleaned;

    if (lastComma > lastDot) {
      normalised = cleaned.replace(/\./g, '').replace(',', '.');
    } else {
      normalised = cleaned.replace(/,/g, '');
    }

    const value = Number.parseFloat(normalised);
    return Number.isFinite(value) ? value : null;
  };

  const getBssResolvedPrice = (probe) => {
    if (!probe) return null;

    /*
     * BSS currently rewrites the probe to:
     * <span>original</span><span>customer price</span>
     * Take the LAST monetary descendant so sale/compare markup is safe.
     */
    const descendants = [...probe.querySelectorAll('span')]
      .map((node) => parseMoneyText(node.textContent))
      .filter((value) => value !== null);

    if (descendants.length) {
      return descendants[descendants.length - 1];
    }

    return parseMoneyText(probe.textContent);
  };

  const formatCents = (cents, currency) => {
    const activeCurrency =
      currency ||
      window.Shopify?.currency?.active ||
      window.Shopify?.currency?.shop_currency ||
      'USD';

    const locale = document.documentElement.lang || 'en-US';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: activeCurrency
      }).format(cents / 100);
    } catch (error) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };

  const resolveB2bVariantPrice = (wrapper) => {
    const probe = wrapper.querySelector('[data-tvati-bss-price-probe]');
    const output = wrapper.querySelector('[data-tvati-b2b-resolved-price]');

    if (!probe || !output) return false;

    const variantRetailCents = Number(wrapper.dataset.variantRetailCents);
    const baseRetailCents = Number(wrapper.dataset.bssBaseRetailCents);

    if (
      !Number.isFinite(variantRetailCents) ||
      !Number.isFinite(baseRetailCents) ||
      baseRetailCents <= 0
    ) {
      return false;
    }

    const bssPrice = getBssResolvedPrice(probe);
    if (bssPrice === null) return false;

    const bssCents = Math.round(bssPrice * 100);
    const factor = bssCents / baseRetailCents;

    /*
     * Ignore the untouched retail probe (factor === 1) while BSS is still
     * loading. Once BSS marks the node active, factor 1 is also accepted in
     * case a valid customer rule genuinely has no discount.
     */
    const bssProcessed =
      probe.hasAttribute('bss-b2b-product-active') ||
      probe.querySelectorAll('span').length > 0;

    if (!bssProcessed) return false;
    if (!Number.isFinite(factor) || factor <= 0 || factor > 2) return false;

    const resolvedCents = Math.round(variantRetailCents * factor);
    const currency = wrapper.dataset.currency;
    const retail = wrapper.querySelector('[data-tvati-b2b-retail-price]');

    output.textContent = formatCents(resolvedCents, currency);
    output.dataset.tvatiB2bResolved = 'true';
    wrapper.dataset.tvatiB2bFactor = factor.toFixed(6);

    if (retail) {
      retail.hidden = resolvedCents === variantRetailCents;
    }

    return true;
  };

  const initB2bVariantPrice = (wrapper) => {
    if (!wrapper || wrapper.dataset.tvatiB2bObserverReady === 'true') return;

    const probe = wrapper.querySelector('[data-tvati-bss-price-probe]');
    if (!probe) return;

    const resolve = () => resolveB2bVariantPrice(wrapper);

    resolve();

    const observer = new MutationObserver(resolve);
    observer.observe(probe, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['bss-b2b-product-active', 'style']
    });

    wrapper.dataset.tvatiB2bObserverReady = 'true';
  };

  const initB2bPrices = (root = document) => {
    if (root.matches?.(B2B_PRICE_SELECTOR)) {
      initB2bVariantPrice(root);
    }

    root.querySelectorAll?.(B2B_PRICE_SELECTOR).forEach(initB2bVariantPrice);
  };

  const initEverything = (root = document) => {
    init(root);
    initB2bPrices(root);
  };

  /*
   * init() has already been wired above for the gallery. Run pricing here too
   * without replacing any of the slider behaviour.
   */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initB2bPrices());
  } else {
    initB2bPrices();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initB2bPrices(event.target);
  });

  document.addEventListener('bss_b2b_doneCheckCp', () => {
    window.requestAnimationFrame(() => {
      document
        .querySelectorAll(B2B_PRICE_SELECTOR)
        .forEach(resolveB2bVariantPrice);
    });
  });

})();
